import { useMemo, useState, type ReactNode } from "react";
import { EmptyState } from "./States";

export interface Column<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  sort?: (row: T) => string | number | null | undefined;
  align?: "right";
  className?: string;
}

interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowClass?: (row: T) => string;
  selectedKey?: string | null;
  empty?: ReactNode;
  initialSort?: { key: string; dir: "asc" | "desc" };
  maxHeight?: number;
  caption?: string;
}

export function DataTable<T>({ rows, columns, rowKey, onRowClick, rowClass, selectedKey, empty, initialSort, maxHeight, caption }: Props<T>) {
  const [sort, setSort] = useState(initialSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sort) return rows;
    const get = col.sort;
    return [...rows].sort((a, b) => {
      const x = get(a), y = get(b);
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      const r = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "en", { numeric: true });
      return sort.dir === "asc" ? r : -r;
    });
  }, [rows, columns, sort]);

  if (!rows.length) return <>{empty ?? <EmptyState title="Nothing to show" />}</>;

  return (
    <div className="table-wrap" style={maxHeight ? ({ "--table-max": `${maxHeight}px` } as React.CSSProperties) : undefined}>
      <table className="table">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th key={c.key} className={c.align === "right" ? "r" : undefined}
                    aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined}>
                  {c.sort ? (
                    <button onClick={() => setSort({ key: c.key, dir: active && sort!.dir === "asc" ? "desc" : "asc" })}>
                      {c.label}
                      <span aria-hidden="true">{active ? (sort!.dir === "asc" ? "▲" : "▼") : ""}</span>
                    </button>
                  ) : c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const k = rowKey(r);
            const cls = [onRowClick ? "clickable" : "", selectedKey === k ? "selected" : "", rowClass?.(r) ?? ""].filter(Boolean).join(" ");
            return (
              <tr key={k} className={cls || undefined} onClick={onRowClick ? () => onRowClick(r) : undefined}>
                {columns.map((c) => (
                  <td key={c.key} className={[c.align === "right" ? "r" : "", c.className ?? ""].join(" ").trim() || undefined}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
